import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Landmark, Banknote, Globe2, Rocket, ExternalLink, Shield, TrendingUp, HandCoins, Building2, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { apiPost } from "@/lib/api";

const CATEGORY_CONFIG = {
  climate_fund: { icon: Globe2, label: "Climate Fund", color: "bg-emerald-500/15 text-emerald-300" },
  regional_bank: { icon: Building2, label: "Regional Bank", color: "bg-blue-500/15 text-blue-300" },
  green_loan: { icon: Banknote, label: "Green Loan", color: "bg-emerald-500/15 text-emerald-300" },
  government_grant: { icon: Landmark, label: "Government Grant", color: "bg-blue-500/15 text-blue-300" },
  eu_fund: { icon: Globe2, label: "EU Fund", color: "bg-violet-500/15 text-violet-300" },
  accelerator: { icon: Rocket, label: "Accelerator", color: "bg-amber-500/15 text-amber-300" },
};

const SUB_CATEGORY_LABELS = {
  grant: "Grant",
  vc_investor: "VC / Investor",
  blended: "Blended Finance",
  green_loan: "Green Loan",
  sustainability_linked: "Sustainability-Linked",
  capex_financing: "Capex Finance",
};

const MATCH_DOT = {
  high: "bg-emerald-400",
  good: "bg-blue-400",
  explore: "bg-amber-400",
};

function SkeletonCard() {
  return (
    <div className="glass-card p-5 space-y-3 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-xl bg-white/5" />
        <div className="w-20 h-5 rounded-full bg-white/5" />
      </div>
      <div className="h-5 w-3/4 rounded bg-white/5" />
      <div className="space-y-2">
        <div className="h-3 w-full rounded bg-white/[0.03]" />
        <div className="h-3 w-5/6 rounded bg-white/[0.03]" />
      </div>
      <div className="h-3 w-1/2 rounded bg-white/[0.03]" />
    </div>
  );
}

export function FundingSection({ companyId }) {
  const [funding, setFunding] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      if (!companyId) return;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const data = await apiPost("/api/funding/generate", { company_id: companyId });
          if (!mountedRef.current) return;
          setFunding(data);
          setLoading(false);
          return;
        } catch (err) {
          if (!mountedRef.current) return;
          if (attempt < 2) await new Promise(r => setTimeout(r, 1200 * (attempt + 1)));
          else toast.error("Could not load funding opportunities.");
        }
      }
      if (mountedRef.current) setLoading(false);
    };
    load();
    return () => { mountedRef.current = false; };
  }, [companyId]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="funding-loading">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 rounded-full bg-primary" />
          <h3 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Support Available</h3>
        </div>
        <div className="rounded-xl border border-primary/15 p-6 flex flex-col items-center gap-4" style={{ background: "linear-gradient(135deg, rgba(74,222,128,0.03) 0%, rgba(2,4,2,0.95) 100%)" }}>
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <div className="text-center">
            <p className="text-white font-medium mb-1">Finding relevant funding</p>
            <p className="text-xs text-white/40">Scanning available grants and subsidies for your profile</p>
          </div>
          <div className="w-full max-w-xs h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full bg-primary/50 animate-[indeterminate_1.5s_ease-in-out_infinite]" style={{ width: '40%' }} />
          </div>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-white/40">Analyzing your company profile for grants, loans & programs</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (!funding || !funding.opportunities?.length) return null;

  return (
    <div className="space-y-4" data-testid="funding-section">
      <div className="flex items-center gap-3">
        <div className="w-1 h-8 rounded-full bg-primary" />
        <div>
          <h3 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Support Available</h3>
          <p className="text-sm text-white/40">Support available for your transition</p>
        </div>
      </div>

      {funding.summary && (
        <p className="text-sm text-white/50 leading-relaxed">{funding.summary}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {funding.opportunities.map((opp, i) => {
          const catConfig = CATEGORY_CONFIG[opp.category] || CATEGORY_CONFIG.eu_fund;
          const Icon = catConfig.icon;
          const hasUrl = opp.url && opp.url.startsWith("http");
          const subLabel = SUB_CATEGORY_LABELS[opp.sub_category] || opp.funding_type || catConfig.label;
          const dotColor = MATCH_DOT[opp.match_level] || MATCH_DOT.good;

          const CardContent = (
            <>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${catConfig.color} border-0 text-xs`}>{subLabel}</Badge>
                  {hasUrl && <ExternalLink className="w-3.5 h-3.5 text-white/20 group-hover:text-primary transition-colors" />}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <h4 className="text-white font-semibold group-hover:text-primary transition-colors">{opp.title}</h4>
                {opp.match_level && (
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} title={opp.match_level === "high" ? "High match" : opp.match_level === "good" ? "Good fit" : "Worth exploring"} />
                )}
              </div>

              {opp.one_liner ? (
                <p className="text-sm text-white/50 leading-relaxed">{opp.one_liner}</p>
              ) : opp.description ? (
                <p className="text-sm text-white/50 leading-relaxed">{opp.description}</p>
              ) : null}

              {opp.ticket_size && (
                <p className="text-xs text-white/30 font-medium">{opp.funding_type} &middot; {opp.ticket_size}</p>
              )}

              {opp.why_you_qualify && (
                <div className="flex items-start gap-1.5">
                  <Star className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-300/60 leading-relaxed">{opp.why_you_qualify}</p>
                </div>
              )}

              {!opp.why_you_qualify && opp.eligibility && (
                <p className="text-xs text-primary/70">{opp.eligibility}</p>
              )}
            </>
          );

          return (
            <motion.div
              key={`fund-${opp.title?.slice(0,20)}-${i}`}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              data-testid={`funding-card-${i}`}
            >
              {hasUrl ? (
                <a
                  href={opp.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-card p-5 space-y-3 block group cursor-pointer hover:border-primary/30 transition-all"
                  data-testid={`funding-link-${i}`}
                >
                  {CardContent}
                </a>
              ) : (
                <div className="glass-card p-5 space-y-3 group">
                  {CardContent}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
